import { Home, Play, Music2, Video, Users, UsersRound, Bell, User } from 'lucide-react';
import { gsap } from 'gsap';
import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { processAvatarUrl } from '@/lib/mediaUtils';

const navItems: { id: string; path: string; icon: typeof Home; label: string }[] = [
  { id: 'home', path: '/feed/forYou', icon: Home, label: 'Home' },
  { id: 'shorts', path: '/shorts', icon: Play, label: 'Shorts' },
  { id: 'music', path: '/music', icon: Music2, label: 'Music' },
  { id: 'video', path: '/video', icon: Video, label: 'Video' },
  { id: 'groups', path: '/groups', icon: UsersRound, label: 'Groups' },
  { id: 'social', path: '/social', icon: Users, label: 'Social' },
];

interface BottomNavProps {
  currentPage: string;
  unreadCount: number;
  hidden?: boolean;
}

export default function BottomNav({ currentPage, unreadCount, hidden = false }: BottomNavProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const navRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (navRef.current) {
      gsap.fromTo(
        navRef.current,
        { y: 100, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.5, ease: 'power2.out', delay: 0.3,
          onComplete: () => {
            // Clear inline transform so Tailwind classes can control it
            if (navRef.current) {
              gsap.set(navRef.current, { clearProps: 'transform,opacity' });
            }
          },
        }
      );
    }
  }, []);

  const handleClick = (path: string, index: number) => {
    const item = itemRefs.current[index];
    if (item) {
      gsap.to(item, {
        scale: 0.9,
        duration: 0.08,
        yoyo: true,
        repeat: 1,
        ease: 'power2.out',
      });
    }
    navigate(path);
  };

  return (
    <>
      <nav
        ref={navRef}
        className={`fixed bottom-0 left-0 right-0 z-50 bg-[#0B0D10]/95 backdrop-blur-xl border-t border-white/[0.06] safe-area-pb transition-transform duration-300 ease-in-out ${hidden ? 'translate-y-full' : 'translate-y-0'}`}
      >
        <div className="flex items-center justify-around h-16 px-1">
          {navItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;

            return (
              <button
                key={item.id}
                ref={(el) => { itemRefs.current[index] = el; }}
                onClick={() => handleClick(item.path, index)}
                className={`flex flex-col items-center justify-center flex-1 h-full relative transition-colors duration-200 ${
                  isActive ? 'text-[#A38BFF]' : 'text-[#A7B0C8] hover:text-[#F5F7FF]'
                }`}
              >
                {isActive && (
                  <span className="absolute -top-0.5 w-6 h-1 bg-[#A38BFF] rounded-full shadow-[0_0_12px_rgba(163,139,255,0.5)]" />
                )}
                <Icon
                  size={22}
                  strokeWidth={isActive ? 2.5 : 2}
                  className={`transition-transform duration-200 ${isActive ? 'scale-110' : ''}`}
                />
                <span className="text-[9px] mt-1 font-medium">{item.label}</span>
              </button>
            );
          })}

          {/* Notifications Button */}
          <button
            onClick={() => navigate('/notifications')}
            className="flex flex-col items-center justify-center flex-1 h-full relative text-[#A7B0C8] hover:text-[#F5F7FF] transition-colors"
          >
            <div className="relative">
              <Bell size={22} strokeWidth={2} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#FF4D4D] rounded-full text-[8px] flex items-center justify-center text-white font-bold">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <span className="text-[9px] mt-1 font-medium">Alerts</span>
          </button>

          {/* Profile Button */}
          <button
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center justify-center flex-1 h-full relative text-[#A7B0C8] hover:text-[#F5F7FF] transition-colors"
          >
            <div className="w-6 h-6 rounded-full overflow-hidden border border-[#A38BFF]/50">
              {user?.avatarUrl ? (
                <img src={processAvatarUrl(user.avatarUrl) || ''} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#A38BFF]/20 flex items-center justify-center">
                  <User size={14} className="text-[#A38BFF]" />
                </div>
              )}
            </div>
            <span className="text-[9px] mt-1 font-medium">Profile</span>
          </button>
        </div>
      </nav>
    </>
  );
}
